import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type {
  NewPackage,
  NewPackageVersion,
  Package,
  PackageSubscriber,
  PackageVersion,
} from "../admin-client";
import {
  useApplications,
  useAppendPackageVersion,
  useArchivePackage,
  useCreatePackage,
  useMigratePackageSubscribers,
  useSetPackageStatus,
  usePackage,
  usePackages,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { StatusBadge, when } from "./common";
import { NotPermitted } from "./packages";

/** The two permissions the Admin API guards this surface with; repeated here
 *  only to decide what to draw. The API re-checks both and audits refusals. */
const READ = "packages:read";
const WRITE = "packages:write";

// --------------------------------------------------------------------------
// Money, as decimal strings
// --------------------------------------------------------------------------

/**
 * Everything below works on the decimal STRING the operator typed, scaled to
 * integers with BigInt. Never a Number: `19.99` through a float is exactly
 * the rounding error the whole platform is arranged to avoid, and this form
 * is where a price is decided.
 *
 * Eight fraction digits because that is what internal/money keeps for a
 * two-decimal currency (Exponent + 6 micros). A JPY figure with seven is
 * refused by the server rather than here — see minorDigits.
 */
const SCALE = 8;
const SCALE_FACTOR = 10n ** BigInt(SCALE);

/** Digits and one dot. The server parses these with internal/money and is the
 *  only thing entitled to decide what they mean; this exists so an operator
 *  who typed "$19,99" hears about it before the reason dialog. */
const DECIMAL = /^\d+(\.\d+)?$/;

/**
 * How many decimal places lie between this currency's major and minor unit —
 * internal/money's `exponents` table, repeated for ONE purpose: deciding
 * whether a price is a whole number of minor units.
 *
 * An unknown code answers undefined and that check is skipped, because the
 * server refuses the currency itself with a better sentence than this form
 * could invent. A guess of 2 here would be wrong for JPY by a factor of 100.
 */
function minorDigits(currency: string): number | undefined {
  switch (currency.trim().toUpperCase()) {
    case "USD":
    case "AUD":
    case "EUR":
    case "GBP":
    case "NZD":
    case "CAD":
    case "SGD":
      return 2;
    case "JPY":
      return 0;
    default:
      return undefined;
  }
}

/** A decimal string as an integer at SCALE fraction digits, or null when it
 *  is not a decimal this form can reason about. */
export function scaleDecimal(value: string): bigint | null {
  const trimmed = value.trim();
  if (!DECIMAL.test(trimmed)) {
    return null;
  }
  const [whole, fraction = ""] = trimmed.split(".");
  if (fraction.length > SCALE) {
    return null;
  }
  return BigInt(whole + fraction.padEnd(SCALE, "0"));
}

/** The inverse, kept to the currency's own precision and never shorter, so
 *  "20" reads as "20.00" beside a price that says "19.99". */
export function formatScaled(units: bigint, currency: string): string {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const whole = (abs / SCALE_FACTOR).toString();
  let fraction = (abs % SCALE_FACTOR).toString().padStart(SCALE, "0").replace(/0+$/, "");
  const least = minorDigits(currency) ?? 2;
  while (fraction.length < least) {
    fraction += "0";
  }
  return `${negative ? "-" : ""}${whole}${fraction === "" ? "" : `.${fraction}`}`;
}

/**
 * Is this price a whole number of minor units?
 *
 * The rule the server states as "a package price is charged to a card and
 * must be a whole number of minor units": a card network settles in cents and
 * cannot be asked for 1999.5 of them. granted_credit gets no such rule — it
 * is spent against a ledger that keeps micros and is never charged to anyone.
 */
export function wholeMinorUnits(value: string, currency: string): boolean {
  const digits = minorDigits(currency);
  const units = scaleDecimal(value);
  if (digits === undefined || units === null) {
    return true;
  }
  return units % 10n ** BigInt(SCALE - digits) === 0n;
}

// --------------------------------------------------------------------------
// The form
// --------------------------------------------------------------------------

const INTERVALS = ["month", "year"];

/** Every field as a string, because that is what an <input> holds. The
 *  conversion to the wire happens once, at submit. */
interface OfferFields {
  application_id: string;
  slug: string;
  name: string;
  description: string;
  sort_order: string;
  currency: string;
  price: string;
  granted_credit: string;
  billing_interval: string;
  interval_count: string;
}

const EMPTY_OFFER: OfferFields = {
  application_id: "",
  slug: "",
  name: "",
  description: "",
  sort_order: "",
  currency: "USD",
  price: "",
  granted_credit: "",
  billing_interval: "month",
  interval_count: "1",
};

/** The priced half of an existing version, as the append form starts from
 *  it: an operator re-pricing a plan is editing what is there, not writing a
 *  plan from nothing. */
function offerFromVersion(version: PackageVersion): OfferFields {
  return {
    ...EMPTY_OFFER,
    currency: version.currency,
    price: version.price,
    granted_credit: version.granted_credit,
    billing_interval: version.billing_interval,
    interval_count: String(version.interval_count),
  };
}

interface OfferErrors {
  price?: string;
  granted?: string;
  intervalCount?: string;
  slug?: string;
  name?: string;
  application?: string;
}

/**
 * The same three refusals internal/admin's parsePackageVersion makes, checked
 * here BEFORE the request.
 *
 * Not a substitute for the server's checks, which stay the guarantee — and
 * not a duplicate of them either: this is the only place the operator finds
 * out while the number is still under their cursor. The reason dialog stands
 * between this form and the request, so a refusal discovered server-side
 * costs the operator a reason code as well as a round trip.
 */
function validate(fields: OfferFields, mode: FormMode): OfferErrors {
  const errors: OfferErrors = {};
  const price = fields.price.trim();
  const granted = fields.granted_credit.trim();

  if (price === "") {
    errors.price = "Required — a package that charges nothing is not an offer.";
  } else if (!DECIMAL.test(price)) {
    errors.price = "Digits and a decimal point only — no symbols, no thousands separators.";
  } else if (scaleDecimal(price) === 0n) {
    errors.price = "A package that charges nothing is not an offer.";
  } else if (!wholeMinorUnits(price, fields.currency)) {
    errors.price = `A price is charged to a card and must be a whole number of ${
      minorDigits(fields.currency) === 0 ? fields.currency.trim().toUpperCase() : "minor units"
    }.`;
  }

  if (granted === "") {
    errors.granted = "Required — a package that grants nothing is not an offer.";
  } else if (!DECIMAL.test(granted)) {
    errors.granted = "Digits and a decimal point only — no symbols, no thousands separators.";
  } else if (scaleDecimal(granted) === 0n) {
    errors.granted = "A package that grants nothing is not an offer.";
  }

  // The surplus IS the offer. A package granting less than it charges is a
  // strictly worse deal than the top-up button beside it, which is never
  // something anybody means to publish — it is a decimal in the wrong place.
  const scaledPrice = scaleDecimal(price);
  const scaledGrant = scaleDecimal(granted);
  if (
    errors.price === undefined &&
    errors.granted === undefined &&
    scaledPrice !== null &&
    scaledGrant !== null &&
    scaledGrant < scaledPrice
  ) {
    errors.granted = `Grant at least the price: ${granted} of credit for a ${price} price is a worse deal than a top-up.`;
  }

  if (!/^\d+$/.test(fields.interval_count.trim()) || Number(fields.interval_count.trim()) < 1) {
    errors.intervalCount = "A whole number of intervals, at least 1.";
  }

  if (mode === "create") {
    if (fields.application_id === "") {
      errors.application = "Choose the application this package sells credit for.";
    }
    if (fields.slug.trim() === "") {
      errors.slug = "Required.";
    }
    if (fields.name.trim() === "") {
      errors.name = "Required.";
    }
  }
  return errors;
}

function versionBody(fields: OfferFields): NewPackageVersion {
  return {
    // Verbatim: these strings reach internal/money exactly as typed. Nothing
    // in this portal multiplies a price on the way out.
    currency: fields.currency.trim(),
    price: fields.price.trim(),
    granted_credit: fields.granted_credit.trim(),
    billing_interval: fields.billing_interval,
    interval_count: Number(fields.interval_count.trim()),
  };
}

type FormMode = "create" | "append";

/**
 * The surplus, computed as the operator types.
 *
 * The one place this console does arithmetic on money, and it is a PREVIEW —
 * the number stored against the package is the server's `bonus`, and every
 * table renders that. It is here because the extra over a top-up is the whole
 * point of the plan being written, and a form that only showed it after the
 * write is a form an operator has to submit to understand.
 */
function BonusPreview({ fields }: { fields: OfferFields }) {
  const price = scaleDecimal(fields.price);
  const granted = scaleDecimal(fields.granted_credit);
  const currency = fields.currency.trim().toUpperCase() || "USD";
  if (price === null || granted === null) {
    return (
      <p className="esm-muted-text">
        Type a price and a credit grant to see what this plan gives over a top-up.
      </p>
    );
  }
  const bonus = granted - price;
  if (bonus < 0n) {
    return (
      <Alert tone="danger" title="This plan is worse than a top-up">
        It charges {fields.price.trim()} {currency} and grants {fields.granted_credit.trim()}{" "}
        {currency} — {formatScaled(-bonus, currency)} {currency} less credit than the same money at
        the top-up counter.
      </Alert>
    );
  }
  return (
    <Alert
      tone={bonus === 0n ? "warning" : "info"}
      title={`Extra over top-up: ${formatScaled(bonus, currency)} ${currency}`}
    >
      {bonus === 0n ? (
        <>
          A subscriber gets exactly what the same money buys at the top-up counter, and pays every{" "}
          {intervalWords(fields)} for it. That is legal, and it is rarely what was meant.
        </>
      ) : (
        <>
          A subscriber pays {fields.price.trim()} {currency} every {intervalWords(fields)} and{" "}
          {formatScaled(granted, currency)} {currency} lands in their subscription bucket — {""}
          {formatScaled(bonus, currency)} {currency} more than that money would buy as a top-up.
          Unspent subscription credit is cleared when the period ends.
        </>
      )}
    </Alert>
  );
}

/** "month" / "3 months" — the interval as a customer experiences it. */
function intervalWords(fields: OfferFields): string {
  const count = Number(fields.interval_count.trim());
  if (!Number.isFinite(count) || count <= 1) {
    return fields.billing_interval;
  }
  return `${count} ${fields.billing_interval}s`;
}

/**
 * One form, two uses: defining a package and re-pricing one.
 *
 * The same component on purpose — the rules a price has to satisfy are the
 * same rules whichever route it is going to, and two copies of them is how
 * one copy ends up permitting something the other refuses.
 */
function OfferForm({
  mode,
  fields,
  setFields,
  errors,
  applications,
}: {
  mode: FormMode;
  fields: OfferFields;
  setFields: (next: OfferFields) => void;
  errors: OfferErrors;
  applications: { id: string; name: string }[];
}) {
  const set = (field: keyof OfferFields) => (value: string) =>
    setFields({ ...fields, [field]: value });

  return (
    <>
      <div className="esm-form-grid">
        {mode === "create" ? (
          <>
            <Field
              label="Application"
              htmlFor="package-form-application"
              hint="Credit bought here is held per application and is not transferable."
              error={errors.application}
            >
              <Select
                id="package-form-application"
                value={fields.application_id}
                onChange={(event) => set("application_id")(event.target.value)}
              >
                <option value="">Choose an application…</option>
                {applications.map((application) => (
                  <option key={application.id} value={application.id}>
                    {application.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Slug"
              htmlFor="package-form-slug"
              hint="Unique within the application, and what the customer portal keys on."
              error={errors.slug}
            >
              <Input
                id="package-form-slug"
                value={fields.slug}
                onChange={(event) => set("slug")(event.target.value)}
              />
            </Field>
            <Field label="Name" htmlFor="package-form-name" error={errors.name}>
              <Input
                id="package-form-name"
                value={fields.name}
                onChange={(event) => set("name")(event.target.value)}
              />
            </Field>
            <Field
              label="Description"
              htmlFor="package-form-description"
              hint="Shown to the customer beside the price. Optional."
            >
              <Input
                id="package-form-description"
                value={fields.description}
                onChange={(event) => set("description")(event.target.value)}
              />
            </Field>
            <Field
              label="Sort order"
              htmlFor="package-form-sort"
              hint="Lower comes first on the plans screen. Empty is 0."
            >
              <Input
                id="package-form-sort"
                inputMode="numeric"
                value={fields.sort_order}
                onChange={(event) => set("sort_order")(event.target.value)}
              />
            </Field>
          </>
        ) : null}
        <Field
          label="Currency"
          htmlFor="package-form-currency"
          hint="The currency the card is charged in."
        >
          <Input
            id="package-form-currency"
            value={fields.currency}
            onChange={(event) => set("currency")(event.target.value)}
          />
        </Field>
        <Field
          label="Price"
          htmlFor="package-form-price"
          hint="Major units, as charged: 20.00"
          error={errors.price}
        >
          <Input
            id="package-form-price"
            inputMode="decimal"
            value={fields.price}
            onChange={(event) => set("price")(event.target.value)}
          />
        </Field>
        <Field
          label="Credit granted"
          htmlFor="package-form-granted"
          hint="What lands in the subscription bucket each time an invoice is paid."
          error={errors.granted}
        >
          <Input
            id="package-form-granted"
            inputMode="decimal"
            value={fields.granted_credit}
            onChange={(event) => set("granted_credit")(event.target.value)}
          />
        </Field>
        <Field label="Billed every" htmlFor="package-form-interval">
          <Select
            id="package-form-interval"
            value={fields.billing_interval}
            onChange={(event) => set("billing_interval")(event.target.value)}
          >
            {INTERVALS.map((interval) => (
              <option key={interval} value={interval}>
                {interval}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Intervals per charge"
          htmlFor="package-form-interval-count"
          hint="1 is monthly or yearly; 3 with 'month' is quarterly."
          error={errors.intervalCount}
        >
          <Input
            id="package-form-interval-count"
            inputMode="numeric"
            value={fields.interval_count}
            onChange={(event) => set("interval_count")(event.target.value)}
          />
        </Field>
      </div>
      <BonusPreview fields={fields} />
    </>
  );
}

// --------------------------------------------------------------------------
// Create
// --------------------------------------------------------------------------

export interface PackageCreateSearch {
  /** Carried from the catalogue's filter so "define a package" from App B's
   *  list opens the form already pointed at App B. */
  application_id?: string;
}

export function PackageCreate({ search }: { search: PackageCreateSearch }) {
  return (
    <RequireOperator>
      {(session) =>
        !can(session.operator, READ) ? (
          <NotPermitted title="New package" permission={READ} />
        ) : !can(session.operator, WRITE) ? (
          <NotPermitted title="New package" permission={WRITE} />
        ) : (
          <PackageCreateForm search={search} />
        )
      }
    </RequireOperator>
  );
}

function PackageCreateForm({ search }: { search: PackageCreateSearch }) {
  const navigate = useNavigate();
  const applications = useApplications();
  const create = useCreatePackage();
  const [fields, setFields] = useState<OfferFields>({
    ...EMPTY_OFFER,
    application_id: search.application_id ?? "",
  });

  const errors = validate(fields, "create");
  const ready = Object.keys(errors).length === 0;

  const body = (): NewPackage => ({
    ...versionBody(fields),
    application_id: fields.application_id,
    slug: fields.slug.trim(),
    name: fields.name.trim(),
    description: fields.description.trim() === "" ? undefined : fields.description.trim(),
    sort_order: fields.sort_order.trim() === "" ? undefined : Number(fields.sort_order.trim()),
  });

  return (
    <>
      <PageHeader
        title="Define a package"
        description={
          <>
            A package is an offer: a price charged every interval, and the credit that lands in the
            subscriber's subscription bucket each time it is paid. ·{" "}
            <Link to="/packages" search={{ application_id: undefined, status: undefined }}>
              back to all packages
            </Link>
          </>
        }
      />
      <Card title="The offer">
        <p className="esm-muted-text">
          It is created as a <strong>draft</strong>: nothing is on sale until it is published, so
          the numbers below can be got wrong and withdrawn without a customer having seen them.
        </p>
        {/* The server's own refusal, verbatim — a duplicate slug and an
            invalid grant are different problems and the operator has to be
            told which one they hit. */}
        <ErrorAlert error={create.error} title="That package was not created" />
        {/* Not a <form>: the write goes through the reason prompt rather than
            a submit, and an Enter key that posted past the prompt would be a
            mutation with no reason on it. */}
        <OfferForm
          mode="create"
          fields={fields}
          setFields={setFields}
          errors={errors}
          applications={applications.data?.applications ?? []}
        />
        <div className="esm-actions">
          <ReasonButton
            variant="primary"
            busy={create.isPending}
            disabled={!ready}
            mutation={{
              title: `Create ${fields.name.trim() || "a package"}`,
              description: (
                <p>
                  A draft package with its first price. Nobody can subscribe until it is published,
                  and the price becomes an append-only history from this moment: it can be
                  superseded, never edited.
                </p>
              ),
              confirmLabel: "Create package",
              run: async (reason) => {
                const result = await create.mutateAsync({ body: body(), reason });
                await navigate({
                  to: "/packages/$packageId",
                  params: { packageId: result.package.id },
                });
              },
            }}
          >
            Create package
          </ReasonButton>
          <Link
            className="esm-button esm-button--ghost"
            to="/packages"
            search={{ application_id: undefined, status: undefined }}
          >
            Cancel
          </Link>
        </div>
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Detail
// --------------------------------------------------------------------------

/**
 * One package: what it costs, every price it has ever had, and who is on it.
 *
 * The history is the point of the page. A subscriber is charged under the
 * version they subscribed on, so "what was this plan in March" is a question
 * a refund argument turns on — and it is answerable only because a re-price
 * appends a row instead of editing one.
 */
export function PackageDetail({ packageId }: { packageId: string }) {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, READ) ? (
          <PackagePanels packageId={packageId} writable={can(session.operator, WRITE)} />
        ) : (
          <NotPermitted title="Package" permission={READ} />
        )
      }
    </RequireOperator>
  );
}

function PackagePanels({ packageId, writable }: { packageId: string; writable: boolean }) {
  const detail = usePackage(packageId);
  const [notice, setNotice] = useState<string | null>(null);

  if (detail.isPending) {
    return <Spinner label="Loading the package" />;
  }
  if (detail.error || !detail.data) {
    return <ErrorAlert error={detail.error} title="Could not load the package" />;
  }

  const { package: pkg, versions, subscribers } = detail.data;

  return (
    <>
      <PageHeader
        title={pkg.name}
        description={
          <>
            <code>{pkg.slug}</code> for {pkg.application_name} ·{" "}
            <Link to="/packages" search={{ application_id: undefined, status: undefined }}>
              back to all packages
            </Link>
          </>
        }
        actions={
          <div className="esm-badge-row">
            <Badge tone={pkg.status === "active" ? "success" : "neutral"}>{pkg.status}</Badge>
            {pkg.current_version ? null : <Badge tone="danger">no price</Badge>}
          </div>
        }
      />
      {notice ? <Alert tone="success" title={notice} /> : null}
      <PackageCard pkg={pkg} />
      {writable ? <PublishCard pkg={pkg} onDone={(message) => setNotice(message)} /> : null}
      <VersionsCard
        pkg={pkg}
        versions={versions}
        writable={writable}
        onDone={(message) => setNotice(message)}
      />
      <SubscribersCard subscribers={subscribers} />
      {writable ? <WithdrawCard pkg={pkg} onDone={(message) => setNotice(message)} /> : null}
    </>
  );
}

function PackageCard({ pkg }: { pkg: Package }) {
  const version = pkg.current_version;
  return (
    <Card title="Package">
      <dl className="esm-detail-list">
        <dt>Identifier</dt>
        <dd>
          <code>{pkg.id}</code>
        </dd>
        <dt>Application</dt>
        <dd>
          {pkg.application_name} <code>{pkg.application_slug}</code>
        </dd>
        <dt>Description</dt>
        <dd>{pkg.description ?? <span className="esm-muted-text">none</span>}</dd>
        <dt>Sort order</dt>
        <dd>{pkg.sort_order}</dd>
        <dt>In force now</dt>
        <dd>
          {version ? (
            <>
              {version.price} {version.currency} every{" "}
              {version.interval_count === 1
                ? version.billing_interval
                : `${version.interval_count} ${version.billing_interval}s`}, granting{" "}
              {version.granted_credit} {version.currency} —{" "}
              <strong>
                {version.bonus} {version.currency}
              </strong>{" "}
              more than the same money as a top-up
            </>
          ) : (
            // Not blank: a package with no open version cannot be subscribed
            // to whatever its status says, and that is not derivable from the
            // status badge above it.
            <span className="esm-muted-text">
              no version in force — nobody can subscribe to this package
            </span>
          )}
        </dd>
        <dt>Subscribers</dt>
        <dd>
          {pkg.subscriber_count.toLocaleString()} live{" "}
          {pkg.has_subscribers ? (
            <span className="esm-muted-text">— it cannot be withdrawn until they are moved</span>
          ) : null}
        </dd>
        <dt>Created</dt>
        <dd>{when(pkg.created_at)}</dd>
        <dt>Updated</dt>
        <dd>{when(pkg.updated_at)}</dd>
      </dl>
    </Card>
  );
}

/**
 * The step between authoring an offer and a customer being able to buy it.
 *
 * A package is born a DRAFT — created, cloned, or copied from a platform
 * default — and nothing else on this console puts it on sale. Until this card
 * existed the route was there and had no caller, so a tenant could write a
 * package, price it, and never sell it.
 *
 * It sits directly under the package itself rather than beside Withdraw at the
 * bottom, because publishing is the ordinary next step and withdrawing is the
 * end of a package's life; a person who has just priced an offer should not
 * have to scroll past its subscribers to find the button they came for.
 *
 * `archived` is not reachable from here. Withdrawing has to refuse while
 * subscribers are still on the package, and that guard belongs to the Withdraw
 * card — a status switch with three options would eventually be a status
 * switch that lost it.
 */
function PublishCard({ pkg, onDone }: { pkg: Package; onDone: (message: string) => void }) {
  const setStatus = useSetPackageStatus();

  // Archived is terminal here: the server refuses every status change on one,
  // so offering a button would be offering a refusal.
  if (pkg.status === "archived") {
    return null;
  }

  const publishing = pkg.status !== "active";
  // A package with no version in force cannot be subscribed to whatever its
  // status says, so publishing one puts an offer on the shelf that answers
  // "no price" to everybody who reaches for it. The server permits it — the
  // states are independent — which is exactly why the console says so.
  const unpriced = !pkg.current_version;

  return (
    <Card title={publishing ? "Publish" : "On sale"}>
      <ErrorAlert error={setStatus.error} title="That status change failed" />
      <p className="esm-muted-text">
        {publishing ? (
          <>
            This package is a <strong>draft</strong>: it is authored but nobody can subscribe to
            it. Publishing puts it on the shelf your customers see for{" "}
            {pkg.application_name}.
          </>
        ) : (
          <>
            This package is <strong>on sale</strong> for {pkg.application_name}. Taking it back
            to draft removes it from the shelf for new subscribers; it does not touch anybody
            already on it, and it is not the same as withdrawing it.
          </>
        )}
      </p>
      {publishing && unpriced ? (
        <Alert tone="warning" title="This package has no version in force">
          <p>
            It can be published, and nobody will be able to subscribe: the shelf reads the
            version in force, and there is none. Add one below first.
          </p>
        </Alert>
      ) : null}
      <div className="esm-actions">
        <ReasonButton
          variant={publishing ? "primary" : "secondary"}
          busy={setStatus.isPending}
          mutation={{
            title: publishing ? `Publish ${pkg.name}` : `Return ${pkg.name} to draft`,
            confirmLabel: publishing ? "Publish" : "Return to draft",
            description: publishing ? (
              <p>
                {pkg.name} goes on sale for {pkg.application_name}, at{" "}
                {pkg.current_version
                  ? `${pkg.current_version.price} ${pkg.current_version.currency}`
                  : "no price — nobody will be able to subscribe"}
                . Existing subscribers are unaffected either way.
              </p>
            ) : (
              <p>
                {pkg.name} leaves the shelf for {pkg.application_name}. The{" "}
                {pkg.subscriber_count.toLocaleString()} live subscriber
                {pkg.subscriber_count === 1 ? "" : "s"} keep the package and keep being billed
                for it — this stops NEW subscriptions and nothing else.
              </p>
            ),
            run: async (reason) => {
              const result = await setStatus.mutateAsync({
                packageID: pkg.id,
                status: publishing ? "active" : "draft",
                reason,
              });
              onDone(
                result.changed
                  ? `${pkg.name} is now ${result.package.status}.`
                  : `${pkg.name} was already ${result.package.status}.`,
              );
            },
          }}
        >
          {publishing ? "Publish" : "Return to draft"}
        </ReasonButton>
      </div>
    </Card>
  );
}

function VersionsCard({
  pkg,
  versions,
  writable,
  onDone,
}: {
  pkg: Package;
  versions: PackageVersion[];
  writable: boolean;
  onDone: (message: string) => void;
}) {
  const [appending, setAppending] = useState(false);

  return (
    <Card
      title="Price history"
      actions={
        writable ? (
          <Button variant="secondary" onClick={() => setAppending((open) => !open)}>
            {appending ? "Close" : "Append version"}
          </Button>
        ) : null
      }
    >
      <p className="esm-muted-text">
        Newest first. A re-price appends a row and closes the one above it — it never edits a
        price, so every subscriber's terms stay readable here for as long as the package exists.
      </p>
      <DataTable<PackageVersion>
        rows={versions}
        rowKey={(row) => row.id}
        empty="This package has never had a price, so nobody can subscribe to it."
        columns={[
          {
            key: "state",
            header: "State",
            render: (row) => (
              <Badge tone={row.in_force ? "success" : "neutral"}>
                {row.in_force ? "in force" : "superseded"}
              </Badge>
            ),
          },
          {
            key: "effective",
            header: "Effective",
            render: (row) => (
              <>
                <div>{when(row.effective_from)}</div>
                <span className="esm-muted-text">
                  {row.effective_to ? `until ${when(row.effective_to)}` : "still open"}
                </span>
              </>
            ),
          },
          // Printed exactly as the wire sent it: decimal strings the server
          // formatted from integer micros.
          {
            key: "price",
            header: "Price",
            align: "end",
            render: (row) => (
              <span className="esm-amount">
                {row.price} <span className="esm-muted-text">{row.currency}</span>
              </span>
            ),
          },
          {
            key: "granted",
            header: "Credit granted",
            align: "end",
            render: (row) => (
              <span className="esm-amount">
                {row.granted_credit} <span className="esm-muted-text">{row.currency}</span>
              </span>
            ),
          },
          {
            key: "bonus",
            header: "Extra over top-up",
            align: "end",
            render: (row) => (
              <span className="esm-amount">
                {row.bonus} <span className="esm-muted-text">{row.currency}</span>
              </span>
            ),
          },
          {
            key: "interval",
            header: "Billed",
            render: (row) =>
              `every ${
                row.interval_count === 1
                  ? row.billing_interval
                  : `${row.interval_count} ${row.billing_interval}s`
              }`,
          },
          {
            key: "stripe",
            header: "At the provider",
            // Absent until the seam has minted them, and a version without
            // them is not subscribable — so "not minted yet" is a sentence
            // rather than an empty cell.
            render: (row) =>
              row.stripe_price_id ? (
                <>
                  <div>
                    <code>{row.stripe_price_id}</code>
                  </div>
                  <span className="esm-muted-text">{row.stripe_product_id}</span>
                </>
              ) : (
                <span className="esm-muted-text">not minted yet</span>
              ),
          },
        ]}
      />
      {writable && appending ? (
        <AppendVersionForm
          pkg={pkg}
          onDone={(message) => {
            setAppending(false);
            onDone(message);
          }}
        />
      ) : null}
    </Card>
  );
}

function AppendVersionForm({ pkg, onDone }: { pkg: Package; onDone: (message: string) => void }) {
  const append = useAppendPackageVersion();
  // Pre-filled from what is in force: a re-price is a change to the current
  // terms, and starting from an empty form invites one field being carried
  // over by hand and mistyped.
  const [fields, setFields] = useState<OfferFields>(
    pkg.current_version ? offerFromVersion(pkg.current_version) : EMPTY_OFFER,
  );

  const errors = validate(fields, "append");
  const ready = Object.keys(errors).length === 0;

  return (
    <div className="esm-subform">
      <h3>Append a version</h3>
      <p className="esm-muted-text">
        This closes the version currently in force and opens a new one from now.{" "}
        <strong>Nobody is re-charged and nobody's terms change today:</strong> every existing
        subscriber keeps the version they subscribed on until their next renewal, and the change
        applies to each of them at that renewal. New subscribers get it immediately.
      </p>
      <ErrorAlert error={append.error} title="That version was not appended" />
      <OfferForm
        mode="append"
        fields={fields}
        setFields={setFields}
        errors={errors}
        applications={[]}
      />
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={append.isPending}
          disabled={!ready}
          mutation={{
            title: `Re-price ${pkg.name}`,
            description: (
              <p>
                {pkg.current_version
                  ? `The current price of ${pkg.current_version.price} ${pkg.current_version.currency} is closed from now and kept in the history.`
                  : "This package has no price in force, so this becomes its first."}{" "}
                {pkg.subscriber_count > 0
                  ? `${pkg.subscriber_count.toLocaleString()} live subscriber(s) keep their existing terms until their next renewal.`
                  : "Nobody is subscribed, so this applies to everyone who subscribes from now."}
              </p>
            ),
            confirmLabel: "Append version",
            run: async (reason) => {
              const result = await append.mutateAsync({
                packageID: pkg.id,
                body: versionBody(fields),
                reason,
              });
              onDone(
                result.previous_version
                  ? `Re-priced from ${result.previous_version.price} to ${result.version.price} ${result.version.currency}, granting ${result.version.granted_credit}. It applies at each subscriber's next renewal.`
                  : `Priced at ${result.version.price} ${result.version.currency}, granting ${result.version.granted_credit}.`,
              );
            },
          }}
        >
          Append version
        </ReasonButton>
      </div>
    </div>
  );
}

function SubscribersCard({ subscribers }: { subscribers: PackageSubscriber[] }) {
  return (
    <Card title="Subscribers">
      <p className="esm-muted-text">
        The live subscriptions on this package. It is also the list that has to be empty — or
        migrated — before the package can be withdrawn.
      </p>
      <DataTable<PackageSubscriber>
        rows={subscribers}
        rowKey={(row) => row.subscription_id}
        empty="Nobody is subscribed to this package."
        columns={[
          {
            key: "subject",
            header: "Subscriber",
            render: (row) =>
              row.user_id ? (
                <>
                  <div>
                    <Link to="/users/$userId" params={{ userId: row.user_id }}>
                      {row.display_name || row.user_id}
                    </Link>
                  </div>
                  <span className="esm-muted-text">{row.email ?? row.subject_type}</span>
                </>
              ) : (
                <>
                  {/* An organization has no page in this console yet, so its
                      id is printed rather than linked to a 404. */}
                  <div>{row.display_name || row.organization_id}</div>
                  <span className="esm-muted-text">{row.subject_type}</span>
                </>
              ),
          },
          { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
          {
            key: "renewal",
            header: "Renews",
            render: (row) =>
              row.cancel_at_period_end ? (
                <>
                  <div>{when(row.current_period_end)}</div>
                  {/* Cancelled-at-period-end is still a live subscription
                      today: the credit is granted and the guard counts it. */}
                  <Badge tone="warning">cancels then</Badge>
                </>
              ) : (
                when(row.current_period_end)
              ),
          },
          {
            key: "version",
            header: "On version",
            // The version they were sold, which is not necessarily the one in
            // force — that is the whole reason the history above exists.
            render: (row) => (
              <code className="esm-short-id" title={row.package_version_id}>
                {row.package_version_id.slice(0, 8)}
              </code>
            ),
          },
          { key: "since", header: "Since", render: (row) => when(row.created_at) },
        ]}
      />
    </Card>
  );
}


// --------------------------------------------------------------------------
// Withdrawal: the archive guard and its escape hatch
// --------------------------------------------------------------------------

/**
 * Withdrawing a package, and the migration that makes it possible.
 *
 * The two live in ONE card because they are one decision — "retire this
 * offer" — split by a rule the operator did not choose: the Admin API refuses
 * to archive a package anybody is still paying for, so the only way through
 * is to move those people first. Putting the migration on another screen
 * would make the refusal read as a dead end.
 *
 * THE CLIENT-SIDE GUARD IS A COURTESY, NOT THE ENFORCEMENT. The button is
 * disabled while the count is above zero so an operator hears the reason
 * before they click; the server still refuses with a 409 that names the
 * count, and that sentence is shown verbatim if the request goes anyway.
 *
 * The whole card is rendered only for `packages:write` — see PackagePanels.
 */
function WithdrawCard({ pkg, onDone }: { pkg: Package; onDone: (message: string) => void }) {
  const archive = useArchivePackage();
  const archived = pkg.status === "archived";

  // Why the withdraw button is dead, as a sentence — or null when it is live.
  // One value rather than a boolean and a string kept in step, because the
  // tooltip and the disabled state must never disagree about the reason.
  const blocked = archived
    ? "This package has already been withdrawn from sale."
    : pkg.has_subscribers
      ? `${pkg.subscriber_count.toLocaleString()} live subscription(s) are still on this package. ` +
        "Migrate them to another package before withdrawing it."
      : null;

  return (
    <Card title="Withdrawal">
      <p className="esm-muted-text">
        Withdrawing takes the package off sale: no new customer can subscribe to it, and it
        disappears from the plan list in the customer portal. Nothing is deleted — every version
        above stays readable, and so does the record of what each subscriber was sold.
      </p>
      <ErrorAlert error={archive.error} title="That package was not withdrawn" />
      {pkg.has_subscribers ? (
        <MigrateSubscribers pkg={pkg} onDone={onDone} />
      ) : (
        <p className="esm-muted-text">
          {archived
            ? "Nobody is on this package."
            : "Nobody is on this package, so it can be withdrawn now."}
        </p>
      )}
      <div className="esm-actions">
        {/* The title sits on a WRAPPER, not on the button: a disabled button
            fires no pointer events in most browsers, so a tooltip written on
            it is a tooltip nobody ever sees — which is the whole explanation
            the operator needs. */}
        <span title={blocked ?? undefined}>
          <ReasonButton
            variant="danger"
            busy={archive.isPending}
            disabled={blocked !== null}
            mutation={{
              title: `Withdraw ${pkg.name} from sale`,
              description: (
                <p>
                  No new customer will be able to subscribe to{" "}
                  <code>{pkg.slug}</code>. The price history is kept and nothing is deleted. This
                  is refused by the server while anybody is still subscribed.
                </p>
              ),
              confirmLabel: "Withdraw package",
              run: async (reason) => {
                const result = await archive.mutateAsync({ packageID: pkg.id, reason });
                onDone(
                  result.changed
                    ? `${pkg.name} is withdrawn from sale. Its price history is kept.`
                    : `${pkg.name} was already withdrawn from sale.`,
                );
              },
            }}
          >
            Withdraw from sale
          </ReasonButton>
        </span>
        {blocked ? <span className="esm-muted-text">{blocked}</span> : null}
      </div>
    </Card>
  );
}

/**
 * Move every live subscriber onto another package.
 *
 * The picker offers packages this migration could actually succeed on: the
 * SAME application, on sale, with a version in force, priced in the same
 * currency. Every one of those is a rule the server enforces and answers with
 * its own sentence — this only spares the operator from picking a target that
 * was never going to work. An offer that is filtered out here is still
 * refused there if it arrives by another route.
 *
 * The currency comparison is against the version in force on the SOURCE. The
 * server compares against more than that — it also reads the currency of each
 * moving subscription's wallet, which is allowed to differ from the offer
 * they were sold — and that is not on this wire, so it stays the server's
 * refusal to make.
 */
function MigrateSubscribers({ pkg, onDone }: { pkg: Package; onDone: (message: string) => void }) {
  const migrate = useMigratePackageSubscribers();
  const [targetID, setTargetID] = useState("");
  // Only the same application's packages are ever fetched: a cross-application
  // target is refused by the server AND by subscriptions_package_scope, so
  // offering one would be offering a mistake.
  const candidates = usePackages({ application_id: pkg.application_id, status: "active" });
  const currency = pkg.current_version?.currency;

  const targets = (candidates.data?.packages ?? []).filter(
    (candidate) =>
      candidate.id !== pkg.id &&
      candidate.status === "active" &&
      candidate.current_version !== null &&
      // An unpriced SOURCE leaves nothing to compare against, so the currency
      // filter is dropped rather than guessed at. Guessing would hide the one
      // target that works.
      (currency === undefined || candidate.current_version.currency === currency),
  );
  const target = targets.find((candidate) => candidate.id === targetID);

  return (
    <div className="esm-subform">
      <h3>Migrate subscribers</h3>
      <p className="esm-muted-text">
        Moves all {pkg.subscriber_count.toLocaleString()} live subscription(s) onto another
        package. <strong>Nobody is re-charged and nobody's terms change today:</strong> each
        subscriber keeps the period they have already paid for, and the new package's price and
        allowance apply to them at their next renewal.
      </p>
      <ErrorAlert error={candidates.error} title="Could not load the packages to move onto" />
      <ErrorAlert error={migrate.error} title="Those subscribers were not migrated" />
      <Field
        label="Move them onto"
        htmlFor="migrate-target"
        hint={
          currency
            ? `Packages sold by ${pkg.application_name} in ${currency} that are on sale and have a price in force.`
            : `Packages sold by ${pkg.application_name} that are on sale and have a price in force.`
        }
      >
        <Select
          id="migrate-target"
          value={targetID}
          onChange={(event) => setTargetID(event.target.value)}
        >
          <option value="">Choose a package…</option>
          {targets.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name} — {candidate.current_version?.price} {candidate.current_version?.currency}
              {" granting "}
              {candidate.current_version?.granted_credit}
            </option>
          ))}
        </Select>
      </Field>
      {candidates.isPending ? (
        <Spinner label="Loading the packages to move onto" />
      ) : targets.length === 0 ? (
        // Not an empty dropdown: "there is nowhere to move them" is a fact
        // the operator has to act on — by publishing or pricing another
        // package — and a blank picker reads as a screen that failed to load.
        <Alert tone="warning" title="There is nowhere to move these subscribers">
          {pkg.application_name} sells no other package that is on sale, priced
          {currency ? ` in ${currency}` : ""}, and has a version in force. Publish or price one
          first — this package cannot be withdrawn until these subscribers have somewhere to go.
        </Alert>
      ) : null}
      <div className="esm-actions">
        <ReasonButton
          variant="secondary"
          busy={migrate.isPending}
          disabled={!target}
          mutation={{
            title: `Migrate ${pkg.name}'s subscribers`,
            description: (
              <p>
                <strong>
                  {pkg.subscriber_count.toLocaleString()} live subscription(s) will move from{" "}
                  {pkg.name} to {target?.name}.
                </strong>{" "}
                Nobody is charged now and nobody's current period changes: each subscriber keeps
                the terms they were sold until their period ends, and the change takes effect at
                their next renewal. Cancelled and expired subscriptions stay where they are.
              </p>
            ),
            confirmLabel: "Migrate subscribers",
            run: async (reason) => {
              const result = await migrate.mutateAsync({
                packageID: pkg.id,
                targetPackageID: targetID,
                reason,
              });
              setTargetID("");
              onDone(migrationSentence(result));
            },
          }}
        >
          Migrate subscribers
        </ReasonButton>
      </div>
    </div>
  );
}

/**
 * The report, as one sentence: how many moved, where to, and WHEN it reaches
 * them.
 *
 * The count alone would be the least useful half. An operator migrates in
 * order to withdraw, and "when does the old package stop mattering" is
 * answered by the bracket the server computed from the rows it actually
 * touched — plus the rows whose date nothing yet knows, which are counted
 * separately so a date that is wrong in the optimistic direction is never
 * printed.
 */
function migrationSentence(result: {
  migrated: number;
  target_package: Package;
  takes_effect_earliest: string | null;
  takes_effect_latest: string | null;
  pending_first_invoice: number;
}): string {
  const moved = `Moved ${result.migrated.toLocaleString()} subscription(s) to ${result.target_package.name}.`;
  const parts = [moved];
  if (result.takes_effect_earliest) {
    parts.push(
      result.takes_effect_latest && result.takes_effect_latest !== result.takes_effect_earliest
        ? `The change reaches them at their next renewal, between ${when(result.takes_effect_earliest)} and ${when(result.takes_effect_latest)}.`
        : `The change reaches them at their next renewal, on ${when(result.takes_effect_earliest)}.`,
    );
  }
  if (result.pending_first_invoice > 0) {
    parts.push(
      `${result.pending_first_invoice.toLocaleString()} of them have not paid a first invoice yet, so their change takes effect at that invoice.`,
    );
  }
  return parts.join(" ");
}
